import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/context/TranslationContext";

interface ProjectItem {
  id: string;
  title: string;
  description: string;
  thumbnail_url: string | null;
  featured: boolean;
  stage: string;
  client: {
    company_name: string;
  } | null;
}

export default function Projects() {
  const { t } = useTranslation();
  const { data: projects, loading: projectsLoading } = useApi<ProjectItem[]>("/projects/public");
  const { data: portfolioItems, loading: portfolioLoading } = useApi<any[]>("/portfolio");
  const [search, setSearch] = useState("");

  const loading = projectsLoading || portfolioLoading;

  const filteredItems = useMemo(() => {
    if (!projects || !portfolioItems) return [];

    // Get IDs of projects that are already in portfolio_items (as strings for safe comparison)
    const portfolioProjectIds = new Set(
      portfolioItems
        .filter((item) => item.id?.startsWith("project-"))
        .map((item) => String(item.id.replace("project-", "")))
    );

    // Filter out projects that are already in portfolio_items
    const ongoingProjects = projects.filter(
      (project) => !portfolioProjectIds.has(String(project.id))
    );

    const lowercasedSearch = search.toLowerCase();
    return ongoingProjects.filter(
      (item) =>
        item.title.toLowerCase().includes(lowercasedSearch) ||
        item.description.toLowerCase().includes(lowercasedSearch) ||
        item.client?.company_name?.toLowerCase().includes(lowercasedSearch)
    );
  }, [projects, portfolioItems, search]);

  const convertDriveLink = (url: string) => {
    if (url.includes("drive.google.com")) {
      const fileMatch = url.match(/\/file\/d\/([^\/]+)/);
      if (fileMatch) {
        return `https://drive.google.com/thumbnail?id=${fileMatch[1]}&sz=w1000`;
      }
      const idMatch = url.match(/[?&]id=([^&]+)/);
      if (idMatch) {
        return `https://drive.google.com/thumbnail?id=${idMatch[1]}&sz=w1000`;
      }
    }
    return url;
  };

  return (
    <div className="min-h-screen bg-deep-black font-inter">
      <Navbar />

      <section className="relative pt-32 pb-20 px-4 md:px-12 lg:px-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://futuristspeaker.com/wp-content/uploads/2026/05/Orbital-Data-Centers-7656.jpg"
            alt="Projects background"
            className="w-full h-full object-cover object-center opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-deep-black/60 via-deep-black/40 to-deep-black" />
          <div className="absolute inset-0 bg-circuit-pattern opacity-30" />
        </div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <span className="inline-block text-electric-blue text-xs font-semibold uppercase tracking-[0.2em] mb-5 px-3 py-1.5 border border-electric-blue/25 rounded-full bg-electric-blue/5">
              {t("projects.hero_badge")}
            </span>
            <h1 className="font-black text-white leading-tight mb-5" style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "clamp(2.5rem, 5vw, 4.5rem)" }}>
              {t("projects.hero_title")} <span className="text-transparent bg-clip-text bg-gradient-to-r from-electric-blue to-cyber-blue">{t("projects.hero_highlight")}</span>
            </h1>
            <p className="text-silver text-base max-w-2xl mx-auto leading-relaxed">
              {t("projects.hero_description")}
            </p>
          </div>

          {/* Search */}
          <div className="mb-12 relative z-10">
            <div className="max-w-md mx-auto">
              <input
                type="text"
                placeholder={t("projects.search_placeholder")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full bg-dark-card border border-dark-border rounded-xl px-4 py-3 text-white placeholder-silver-dim focus:outline-none focus:border-electric-blue/50 transition-colors"
                style={{
                  backgroundColor: "var(--color-bg-card)",
                  color: "var(--color-text-primary)",
                }}
              />
            </div>
          </div>

           {/* Grid */}
           {loading ? (
             <div className="flex items-center justify-center py-20">
               <div className="w-10 h-10 border-2 border-electric-blue border-t-transparent rounded-full animate-spin" />
             </div>
           ) : (
             <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
               {filteredItems.map((item) => (
                 <Link
                   key={item.id}
                   to={`/projects/${item.id}`}
                   className="group bg-dark-card border border-dark-border rounded-2xl overflow-hidden hover:border-electric-blue/30 transition-all duration-300"
                 >
                   <div className="relative aspect-video overflow-hidden">
                     <img
                       src={convertDriveLink(item.thumbnail_url || "") || "https://via.placeholder.com/800x500?text=No+Image"}
                       alt={item.title}
                       referrerPolicy="no-referrer"
                       className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                     />
                     <div className="absolute inset-0 bg-gradient-to-t from-deep-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                   </div>
                   <div className="p-5">
                     <div className="flex items-center gap-2 mb-3">
                       <span className="text-xs px-2.5 py-1 rounded-full bg-electric-blue/10 border border-electric-blue/20 text-electric-blue font-medium">
                         {item.stage.charAt(0).toUpperCase() + item.stage.slice(1)}
                       </span>
                     </div>
                     <h3 className="text-white font-bold text-base mb-2 group-hover:text-electric-blue transition-colors" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                       {item.title}
                     </h3>
                     <p className="text-silver text-sm leading-relaxed line-clamp-2 mb-4">
                       {item.description}
                     </p>
                     <p className="text-silver-dim text-xs">
                       {item.client?.company_name || "N/A"}
                     </p>
                   </div>
                 </Link>
               ))}
             </div>
           )}

           {(!loading && filteredItems.length === 0) && (
             <div className="text-center py-20">
               <i className="ri-folder-line text-4xl text-silver-dim mb-3 block" />
               <p className="text-silver text-sm">{t("projects.no_projects")}</p>
             </div>
           )}
        </div>
      </section>

      <Footer />
    </div>
  );
}